// @ts-check
// =============================================================================
// js/render/card-tile.js — the card/tile generator, wired into the new client.
//
// WHAT THIS IS
//   A thin ESM adapter around the PRESERVED extraction
//   new-client/card-tile-generator.js (UMD; see its header + README). That
//   file keeps the shipping client's exact pixel layout for cards and board
//   tiles — we do not fork or edit it; we initialize it once with the new
//   client's state and re-export the pieces screens need.
//
// WHAT INIT DOES HERE
//   - Supplies the bundled pixi.js v8 namespace (decision #4: npm, no CDN).
//   - Points the frame textures at OUR server (/cardframes2 fallback to the
//     bucket copies in config if missing) — actually the bucket frames stay
//     the default; they're public and cached hard.
//   - Passes store.cards / userData so the censor + id lookups work.
//   - Skips the generator's WebFont path (loadFonts:false): fonts are
//     self-hosted @font-face; we await document.fonts ourselves.
//   - Creates a hidden offscreen PIXI.Application used ONLY to rasterize
//     cards/tiles to <img> (the duel has its own on-screen Application; PIXI
//     containers from PixiCard/PixiTile work in any stage).
//
// USAGE
//   import { initCardRenderer, gen } from '../render/card-tile.js';
//   await initCardRenderer();
//   const img = await gen.renderCardToImage(card);           // gallery grids
//   const tile = await gen.PixiTile(card, 0, 0, 'JustRender'); // duel board
// =============================================================================

import * as PIXI from 'pixi.js';
// The preserved UMD module: esbuild loads it as CommonJS (module.exports).
import CardTileGenerator from '../../../card-tile-generator.js';
import { store } from '../core/store.js';
import { fetchArt } from '../net/cdn.js';

/** Re-export the generator API under a short name for call sites. */
export const gen = CardTileGenerator;

/** Resolves once init has completed (guards double-init). */
let readyPromise = /** @type {Promise<void>|null} */ (null);

/**
 * Make sure the three game fonts are actually loaded before PIXI draws text
 * (PIXI rasterizes text with whatever font is available at draw time — an
 * unloaded font silently falls back to serif and bakes it into the texture).
 * @returns {Promise<void>}
 */
export async function ensureFonts() {
	try {
		await Promise.all([
			document.fonts.load('36px "Luckiest Guy"'),
			document.fonts.load('36px "Londrina Solid"'),
			document.fonts.load('36px "Merriweather"'),
		]);
		await document.fonts.ready;
	} catch {
		// Font Loading API unavailable — PIXI will use fallbacks.
	}
}

/**
 * Initialize the generator once (idempotent — every screen that renders cards
 * calls this first and awaits it).
 * @returns {Promise<void>}
 */
export function initCardRenderer() {
	if (readyPromise) return readyPromise;
	readyPromise = (async () => {
		// Mipmaps for every texture created from here on: card frames, arts,
		// and text canvases all get MINIFIED hard in the duel (a 778×1082 card
		// drawn ~150px wide) — without mips that minification aliases into
		// jagged edges and unreadable text.
		PIXI.TextureSource.defaultOptions.autoGenerateMipmaps = true;
		await ensureFonts();
		await CardTileGenerator.init({
			PIXI,
			cards: store.cards,
			userData: store.userData,
			loadFonts: false, // self-hosted @font-face already loaded above
			config: {
				// Frame/board textures are self-hosted (decision #4) — the complete
				// set lives in public/cardframes2/ (bucket copy kept as reference).
				frameBase: '/cardframes2',
				fallbackArt: '/cards/Error.png',
				placeholderArt: '/cards/Missing.png',
				// CDN offload: the generator fetches raw art through this instead of
				// bare fetch(), so it tries the Cloudflare edge first and falls back
				// to the GCS bucket. Inert when the offload is disabled. storageBase
				// stays the bucket (the reliable origin/fallback). See js/net/cdn.js.
				artFetch: fetchArt,
			},
			handlers: {
				// Screens attach their own PIXI listeners; the generator's baked-in
				// click paths must stay silent.
				click: () => {},
				addCard: () => {},
				removeCard: () => {},
			},
		});
		CardTileGenerator.registerArts(store.cards);
		// Pre-cache the placeholder/fallback art: the generator uses
		// PIXI.Sprite.from(url), which (in v8) wants the texture already in the
		// Assets cache.
		await Promise.all([
			PIXI.Assets.load('/cards/Missing.png').catch(() => {}),
			PIXI.Assets.load('/cards/Error.png').catch(() => {}),
		]);
	})();
	return readyPromise;
}

/**
 * Would this art file pass the player's content + gender filters?
 *
 * The validation rules mirror the legacy CensorCard exactly (they're the
 * contract with the art data): the art's flags (nude/smut/vore/gore) checked
 * against settings.censor, and the art filename's gender suffix (everything
 * after the third underscore, e.g. "0001_0002_Artist_female_female.jpg" ->
 * "female_female") checked against settings.gender. "generic" and unknown
 * suffixes pass (no toggle governs them).
 *
 * FAIL-OPEN on missing metadata (art_id not in the card's newarts) — same as
 * legacy. Flip the `strict` flag to fail-closed if art data ever gets ahead
 * of the shipped card table.
 *
 * @param {any} baseCard   The card DEFINITION from store.cards (has newarts).
 * @param {string} artFile e.g. "0001_0002_Mamabliss_female_female.jpg".
 * @param {boolean} [strict=false]  Unknown art id -> disallow.
 * @returns {boolean}
 */
export function isArtAllowed(baseCard, artFile, strict = false) {
	const settings = store.userData?.settings;
	if (!settings || !artFile) return true; // censor is opt-in (no session yet)

	// The EGG art family ("0000_<heroEgg>_MinaHyena_generic.jpg", minted
	// server-side per hero — engine MakeEgg) belongs to card 0, which has NO
	// newarts metadata: strict validation would reject it and every played
	// egg would render the placeholder forever. The set is generic/wholesome
	// by design, so it is always allowed.
	if (String(artFile).split('_')[0] === '0000') return true;

	const artStyle = baseCard?.newarts?.find((item) => item.art_id == Number(String(artFile).split('_')[1]));
	if (!artStyle) return !strict;

	// Content flags: a set censor flag hides matching art.
	if (settings.censor) {
		for (const flag of ['nude', 'smut', 'vore', 'gore']) {
			if (artStyle[flag] && settings.censor[flag]) return false;
		}
	}
	// Gender suffix: a disabled gender/pairing toggle hides matching art.
	const suffix = String(artFile).slice(0, String(artFile).lastIndexOf('.')).split('_').slice(3).join('_');
	if (suffix && settings.gender && settings.gender[suffix] === false) return false;
	return true;
}

/**
 * Resolve + VALIDATE which art a card instance shows BEFORE rendering it —
 * the pre-pass the old client ran ahead of every PixiCard/PixiTile
 * (PixiRenderCardsBasic :5591, whose SRC ended in CensorCard).
 *
 * Two jobs, every render:
 *   1. RESOLVE — duel clones and saved-deck entries (selectedArt: 0) arrive
 *      with no usable art: pick one via gen.SRC() (the player's server-
 *      filtered collectedArts, newarts fallback).
 *   2. VALIDATE — a PRE-SET selectedArt is never trusted: stale picks from
 *      before a settings change, and the OPPONENT's cosmetic choices arriving
 *      in duel state, must still pass MY filters (isArtAllowed). A violating
 *      art re-picks from my collectedArts — deterministically (keyed off the
 *      offending art id), so the replacement is stable across the full-state
 *      duel updates instead of re-rolling every action like the legacy
 *      CensorCard did.
 *
 * @param {any} card  Mutated: selectedArt gets resolved/validated.
 * @param {boolean} [strict=false]  DUEL rendering passes true: an art with
 *   UNKNOWN metadata is rejected instead of fail-open — the opponent's
 *   cosmetic choice arrives in duel state and could otherwise smuggle an
 *   unfilterable file past the content/gender preferences. (Own screens keep
 *   fail-open so legacy owned art without metadata still shows.)
 * @returns {any}     The same card, for call-site convenience.
 */
export function resolveCardArt(card, strict = false) {
	try {
		if (!card.selectedArt || card.selectedArt == '0') {
			// DETERMINISTIC pick (not the legacy random): duel states resend
			// unset arts on every update, so a random pick would re-roll — and
			// re-render — every card on every action. Preference order:
			//   1. the player's gallery choice (base.selectedArt), if it passes
			//      the filters,
			//   2. a stable slot in their filtered collectedArts (keyed by card
			//      id, so every copy + every update agrees),
			//   3. the legacy gen.SRC chain (newarts fallback; caches its pick
			//      on the base card, so it's stable too).
			const base = store.cards[card.id];
			if (base?.selectedArt && isArtAllowed(base, base.selectedArt, strict)) {
				card.selectedArt = base.selectedArt;
				return card;
			}
			const candidates = (base?.collectedArts || []).filter((art) => isArtAllowed(base, art));
			if (candidates.length) {
				card.selectedArt = candidates[card.id % candidates.length];
				return card;
			}
			gen.SRC(card);
			return card;
		}
		const base = store.cards[card.id];
		if (isArtAllowed(base, card.selectedArt, strict)) {
			return card;
		}
		// Filtered: replace from MY collectedArts (server-filtered = safe),
		// double-checked through the same validator (belt + braces). The
		// deterministic index keeps the swap stable for this card.
		const offendingId = Number(String(card.selectedArt).split('_')[1]) || 0;
		const candidates = (base?.collectedArts || []).filter((art) => isArtAllowed(base, art));
		card.selectedArt = candidates.length
			? candidates[offendingId % candidates.length]
			: undefined; // nothing safe to show -> placeholder art window
	} catch (error) {
		console.error('resolveCardArt failed for', card?.name, error);
	}
	return card;
}

/**
 * Re-point the generator at fresh data after "Save Settings" replaces the
 * whole card list (settings.md: saving re-derives the collection). init() is
 * called again WITH the existing app, so no new canvas is created — only the
 * cards/userData references update.
 * @returns {Promise<void>}
 */
export async function refreshCardData() {
	if (!readyPromise) return;
	await readyPromise;
	const { p } = CardTileGenerator._state();
	await CardTileGenerator.init({
		PIXI,
		app: p,
		cards: store.cards,
		userData: store.userData,
		loadFonts: false,
		handlers: { click: () => {}, addCard: () => {}, removeCard: () => {} },
	});
	CardTileGenerator.registerArts(store.cards);
}
